import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { useAuthStore } from "@/stores/auth.ts";

interface Props {
  /** Roles permitidos. Si el user.role no está aquí → 403. */
  allow: ReadonlyArray<string>;
  children: ReactNode;
}

/**
 * Protege rutas que requieren un rol específico (admin, owner, etc.).
 * Si no hay sesión, redirige a /login. Si hay sesión pero el rol no está
 * permitido, muestra una pantalla 403 explícita.
 */
export function RequireRole({ allow, children }: Props) {
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const location = useLocation();

  if (!token || !user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (!allow.includes(user.role)) {
    return <ForbiddenPage userRole={user.role} />;
  }

  return <>{children}</>;
}

function ForbiddenPage({ userRole }: { userRole: string }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-50 p-6 text-center">
      <h1 className="text-2xl font-bold text-slate-900">Acceso denegado</h1>
      <p className="max-w-md text-slate-600">
        Tu rol actual (<code className="rounded bg-slate-100 px-2 py-0.5">{userRole}</code>) no
        tiene permiso para esta sección.
      </p>
      <a
        href="/"
        className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
      >
        Volver
      </a>
    </div>
  );
}
